import { useState } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import Breadcrumbs from "@/nav/Breadcrumbs";
import Customer360Drawer from "@/components/Customer360Drawer";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useBranding } from "@/lib/branding";
import { APP_VERSION, COPYRIGHT_HOLDER } from "@/lib/version";
import { X } from "@phosphor-icons/react";

// DSG-001 compliant — all surfaces and dividers reference semantic tokens.
export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { host } = useBranding();
  const footerLabel = host?.name || "Fuel Order Centre";

  return (
    <div className="flex min-h-screen bg-surface">
      {/* Desktop sidebar */}
      <div className="hidden lg:block">
        <div className="fixed inset-y-0 left-0 w-60 border-r border-divider bg-surface-alt">
          <Sidebar />
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-sidebar-overlay">
          <div
            className="absolute inset-0 bg-surface-inverse/60"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative h-full w-60 bg-surface-alt">
            <button
              data-testid="mobile-sidebar-close"
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-3 z-10 rounded-md p-1.5 text-ink-secondary hover:bg-surface-alt"
              aria-label="Close menu"
            >
              <X size={18} weight="bold" />
            </button>
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex min-h-screen w-full flex-col lg:pl-60">
        <Header onMenuClick={() => setMobileOpen(true)} />
        <Breadcrumbs />
        <main className="flex-1 bg-surface px-5 py-6 sm:px-8 lg:px-10 lg:py-8">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
        <footer
          className="border-t border-divider bg-surface px-6 py-3 text-xs text-ink-muted"
          data-testid="app-footer"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span data-testid="footer-app-name" className="font-semibold text-ink-secondary">
              {footerLabel}
            </span>
            <span data-testid="footer-version" className="font-mono">
              Version {APP_VERSION}
            </span>
            <span data-testid="footer-copyright">
              © {COPYRIGHT_HOLDER}
            </span>
          </div>
        </footer>
      </div>

      {/* Global Customer 360 drawer — URL-controlled (?customer=<id>). */}
      <Customer360Drawer />
    </div>
  );
}
